import {t, Trans} from '@lingui/macro';
import _, {filter} from 'lodash';
import React, {Component, createRef, RefObject} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {composerDetailsTokenBoxStyle} from '../../../../../libs/shared/ui/src/styles/composerStyles';
import {
  InternationalizationContextProps,
  withInternationalization,
} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {buildTeammateDisplayName} from '../../../../helpers/modelHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {FindOnlineStatus} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {TeammateToken} from '../../../common/editor/tokens/teammateToken';
import {TokenBase} from '../../../common/editor/tokens/tokenBase';
import {TokensEditor} from '../../../common/editor/tokens/tokensEditor';
import {InternalRecipientsDropdownContainer} from './internalRecipientsDropdownContainer';

/*
 * Props.
 */

export interface InternalRecipientsEditorProps {
  teammates: ReadonlyArray<TeammateModel>;
  inbox: InboxModel | undefined;
  findOnlineStatus: FindOnlineStatus;
  onTeammatesChange: HandlerOf<ReadonlyArray<TeammateModel>>;
  onInboxChange: HandlerOf<InboxModel | undefined>;
}

/*
 * Style.
 */

const StyledTeammatesDiv = styled.div`
  display: grid;
  grid-template: 'to editor';
  grid-template-columns: auto 1fr;
  grid-column-gap: 6px;

  padding: 18px 16px 2px 16px;
`;

const StyledRoleDiv = styled.div`
  grid-area: to;
  ${composerDetailsTokenBoxStyle};
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledEditorDiv = styled.div`
  grid-area: editor;
`;

/*
 * Component.
 */

enum InternalRecipientTypesEnum {
  INBOX = 'inbox',
  TEAMMATE = 'teammate',
}

interface InternalRecipient {
  id: number;
  type: InternalRecipientTypesEnum;
}

interface InternalRecipientsEditorState {
  text: string;
  isDropdownVisible: boolean;
  sortedInternalRecipients: ReadonlyArray<InternalRecipient>;
}

class InternalRecipientsEditorComponent extends Component<
  InternalRecipientsEditorProps & InternationalizationContextProps,
  InternalRecipientsEditorState
> {
  constructor(props: InternalRecipientsEditorProps & InternationalizationContextProps) {
    super(props);

    const sortedInternalRecipients = _([
      props.inbox ? {type: InternalRecipientTypesEnum.INBOX, id: props.inbox.id} : undefined,
      props.teammates.map((teammate) => ({type: InternalRecipientTypesEnum.TEAMMATE, id: teammate.id})),
    ])
      .compact()
      .flatten()
      .value();

    this.state = {
      text: '',
      isDropdownVisible: true,
      sortedInternalRecipients,
    };
    this.tokensEditorRef = createRef<TokensEditor>();
  }

  private tokensEditorRef: RefObject<TokensEditor>;

  /*
   * Event handlers.
   */

  private addTeammate: HandlerOf<TeammateModel> = (teammate) => {
    const newTeammates = [...this.props.teammates, teammate];
    this.props.onTeammatesChange(newTeammates);

    this.setState((state) => {
      const sortedInternalRecipients = [
        ...state.sortedInternalRecipients,
        {id: teammate.id, type: InternalRecipientTypesEnum.TEAMMATE},
      ];

      return {text: '', sortedInternalRecipients};
    });
  };

  private addInbox: HandlerOf<InboxModel> = (inbox) => {
    this.props.onInboxChange(inbox);

    this.setState((state) => {
      const sortedInternalRecipients = [
        ...state.sortedInternalRecipients,
        {id: inbox.id, type: InternalRecipientTypesEnum.INBOX},
      ];

      return {text: '', sortedInternalRecipients};
    });
  };

  private renderTokenForCopy = (index: number) => {
    const recipient = this.state.sortedInternalRecipients[index];
    if (recipient.type === InternalRecipientTypesEnum.INBOX) {
      return this.props.inbox ? this.props.inbox.name : '';
    }

    const teammate = this.props.teammates[index];
    if (!teammate) {
      return '';
    }

    return buildTeammateDisplayName(teammate);
  };

  private hideDropdown = () => {
    this.setState({isDropdownVisible: false});
  };

  private onTextChange = (newText: string) => {
    this.setState({
      text: newText,
      isDropdownVisible: true,
    });
  };

  private removeInternalRecipients: HandlerOf<ReadonlyArray<number>> = (internalRecipientIndexes) => {
    this.setState(
      (state) => {
        const newInternalRecipients = filter(
          state.sortedInternalRecipients,
          (_r, index) => !internalRecipientIndexes.includes(index),
        );

        // Update component state.
        return {
          sortedInternalRecipients: newInternalRecipients,
        };
      },
      () => {
        // Remove teammates.
        const teammateIds = this.state.sortedInternalRecipients
          .filter((r) => r.type === InternalRecipientTypesEnum.TEAMMATE)
          .map((r) => r.id);
        this.props.onTeammatesChange(
          this.props.teammates.filter((teammate) => teammateIds.includes(teammate.id)),
        );

        // Remove inbox.
        const inboxRecipient = this.state.sortedInternalRecipients.find(
          (r) => r.type === InternalRecipientTypesEnum.INBOX,
        );
        if (!inboxRecipient) {
          this.props.onInboxChange(undefined);
        }
      },
    );
  };

  /*
   * Render.
   */

  render() {
    return (
      <StyledTeammatesDiv>
        <StyledRoleDiv>
          <Trans id="internal-composer-header-to">To:</Trans>
        </StyledRoleDiv>
        <StyledEditorDiv>
          <TokensEditor
            ref={this.tokensEditorRef}
            shouldFocus
            tokenIds={this.state.sortedInternalRecipients.map((r) => `${r.type}_${r.id}`)}
            placeholder={this.buildPlaceholder()}
            renderToken={this.renderInternalRecipient}
            renderTokenForCopy={this.renderTokenForCopy}
            text={this.state.text}
            onTextChange={this.onTextChange}
            onRemoveTokensClick={this.removeInternalRecipients}
            inputId="internal-recipients-editor"
          />
          {this.maybeRenderDropdown()}
        </StyledEditorDiv>
      </StyledTeammatesDiv>
    );
  }

  private renderInternalRecipient = (index: number, isSelected: boolean) => {
    const recipient = this.state.sortedInternalRecipients[index];
    if (recipient.type === InternalRecipientTypesEnum.INBOX) {
      return this.props.inbox && <TokenBase isSelected={isSelected}>{this.props.inbox.name}</TokenBase>;
    }

    const teammateToRender = this.props.teammates.find((teammate) => teammate.id === recipient.id);
    return teammateToRender && <TeammateToken teammate={teammateToRender} isSelected={isSelected} />;
  };

  private maybeRenderDropdown() {
    const tokensEditor = this.tokensEditorRef.current;
    if (!tokensEditor) {
      return null;
    }

    const {isDropdownVisible, text} = this.state;
    if (!isDropdownVisible) {
      return null;
    }

    const query = text.trim();
    if (query.length === 0) {
      return null;
    }

    return (
      <InternalRecipientsDropdownContainer
        anchor={tokensEditor.getAnchor()}
        findOnlineStatus={this.props.findOnlineStatus}
        onTeammateSelect={this.addTeammate}
        onInboxSelect={this.addInbox}
        onRequestClose={this.hideDropdown}
        query={query}
        isInboxSelected={this.props.inbox !== undefined}
        teammates={this.props.teammates}
      />
    );
  }

  private buildPlaceholder() {
    return this.props.localize(t('internal-recipients-placeholder')`Add teammates or an inbox`);
  }
}

export const InternalRecipientsEditor = withInternationalization(InternalRecipientsEditorComponent);
